import React, {useEffect, useImperativeHandle, useRef, useState} from 'react';
import {IDomEditor, IEditorConfig, IToolbarConfig} from '@wangeditor-next/editor'

import '@wangeditor-next/editor/dist/css/style.css'
import {formatter, request} from "@/utils/request";
import {Enum} from "@/enums/system";
import {message} from 'antd';
import {parseUrl} from "@/components/WdiControls/WdiFileUpload";
import {Editor, Toolbar} from '@wangeditor-next/editor-for-react';
import {WdiModal} from "@/components/WdiControls/WdiModalForm";
import "./WdiEditor.less"


type BaseEditorProps = {
  value?: string,
  onChange?: (h5Str: string) => void;
  readOnly?: boolean;
  onClick?: () => void;
  style?: any;
}
type WdiEditorProps = {
  setRef?: (ref: any) => void;
} & BaseEditorProps

type WdiEditorPopProps = {
  visible?: boolean
  onCancel?: () => void
} & BaseEditorProps

type WdiImg = {
  id: string,
  url: string,
  name: string
}

type WdiImgRoute = {
  reloadKey: number,
  id: Map<string, WdiImg>
  url: Map<string, WdiImg>
}

const emptyStrArr = [
  '<p><br></p>',
  '<p><br/></p>'
];


const parseHtml = async (imgRoute: WdiImgRoute, htmlStr?: string) => {
  if (!htmlStr) return {innerHtml: htmlStr ?? ''};
  const domParser = new DOMParser();
  const doc = domParser.parseFromString(htmlStr, 'text/html');
  const imgDoms = doc?.querySelectorAll("img")
  if (!imgDoms || imgDoms.length == 0) return {innerHtml: htmlStr ?? ''};
  for (let i = 0; i < imgDoms.length; i++) {
    const imgDom = imgDoms[i]
    const imgId = imgDom.getAttribute("wdi-path");
    const imgName = imgDom.getAttribute("alt");
    if (!imgId) continue;
    let imgObj = imgRoute.id.get(imgId);
    if (!imgObj) {
      const url = await parseUrl({url: imgId, name: imgName})
      imgObj = {
        id: imgId,
        url: url,
        name: imgName ?? ''
      };
      imgRoute.id.set(imgId, imgObj)
      imgRoute.url.set(url, imgObj)
    }
    imgDom.setAttribute("src", imgObj?.url ?? '')
  }

  return {
    imgRoute: {reloadKey: imgRoute.reloadKey + 1, id: imgRoute.id, url: imgRoute.url},
    innerHtml: doc?.body?.innerHTML ?? ''
  };
}

const getHtml = async (imgRoute: WdiImgRoute, html?: string) => {
  return new Promise(resolve => {
    setTimeout(() => {
      if (!html) {
        resolve(html)
        return;
      }
      const domParser = new DOMParser();
      const doc = domParser.parseFromString(html, 'text/html');
      const imgDoms = doc?.querySelectorAll("img")
      if (!imgDoms) {
        resolve(html)
        return;
      }
      for (let i = 0; i < imgDoms.length; i++) {
        const imgDom = imgDoms[i]
        const imgId = imgDom.getAttribute("wdi-path");
        const src = imgDom.getAttribute("src");
        if (imgId || !src) continue;
        const imgObj = imgRoute.url.get(src);
        if (!imgObj) continue;
        imgDom.setAttribute("wdi-path", imgObj.id)
      }
      const result = doc?.body?.innerHTML ?? '';
      resolve(result);
    }, 50);
  });
}

/**
 * 富文本输入组件
 * @param props
 * @constructor
 */
export const WdiEditor: React.FC<WdiEditorProps> = (props) => {
  const [popVisible, setPopVisible] = useState<boolean>(false);
  const ref = useRef<any>();

  useEffect(() => {
    props.setRef?.(ref);
  }, [ref])

  return (
    <>
      <BaseEditor
        ref={ref}
        value={props.value}
        onChange={props.onChange}
        onClick={() => setPopVisible(true)}
        readOnly={props.readOnly}
        style={props.readOnly ? {cursor: 'pointer'} : {}}
      />
      {
        props.readOnly &&
        <WdiEditorPop
          style={{height: 'auto'}}
          value={props.value} visible={popVisible}
          onCancel={() => setPopVisible(false)}
        />
      }
    </>
  )
}

/**
 * 富文本弹窗
 * @param props
 * @constructor
 */
const WdiEditorPop: React.FC<WdiEditorPopProps> = (props) => {
  return (
    <>
      <WdiModal
        visible={props?.visible}
        width={1200}
        bodyStyle={{whiteSpace: "pre-line", maxWidth: 1200}}
        footer={false}
        maskClosable={true}
        closeIcon={""}
        closable={false}
        onCancel={props.onCancel}
      >
        <BaseEditor style={props.style} value={props.value} readOnly={true}/>
      </WdiModal>
    </>
  )
};

/**
 * 基础组件
 */
const BaseEditor = React.forwardRef((props: BaseEditorProps, ref) => {
  const [editor, setEditor] = useState<IDomEditor>();
  const [html, setHtml] = useState<string>('');
  const [imgRoute, setImgRoute] = useState<WdiImgRoute>({
    reloadKey: 0,
    id: new Map<string, WdiImg>(),
    url: new Map<string, WdiImg>()
  });

  useImperativeHandle(ref, () => {
    return {
      getHtml: () => {
        props.onChange?.(emptyStrArr.includes(html) ? '' : html)
        return getHtml(imgRoute, html)
      },
    }
  })


  //编辑器配置
  const editorConfig: Partial<IEditorConfig> = {
    onCreated: (editor) => {
      setEditor(editor);
    },
    onChange: (editor) => setHtml(editor?.getHtml()),
    placeholder: props?.readOnly ? '' : '请输入内容...',
    readOnly: props?.readOnly ?? false,
    MENU_CONF: {
      uploadImage: {
        async customUpload(file: File, insertFn: any) {
          const formData = new FormData();
          let generateTypeValue: number = Enum.AttachmentGenerateType.DEFAULT;
          formData.append('fileInfo', file);
          formData.append('generateType', String(generateTypeValue)); //用户上传附件
          request<API.ResponseResult<API.FileItem>>(
            '/fileapi/file/upload',
            formatter({method: 'POST', body: formData}),
          ).then(res => {
            const fileItem: any = {...(res?.data ?? {})};
            fileItem.url = res.data?.path;
            if (fileItem?.url) {
              parseUrl(fileItem).then(async url => {
                await insertFn(url, fileItem.name, undefined);
                imgRoute.url.set(
                  url,
                  {
                    id: fileItem?.url,
                    url: url,
                    name: fileItem?.name
                  }
                )
                setImgRoute({reloadKey: imgRoute.reloadKey + 1, id: imgRoute.id, url: imgRoute.url})
              })
            }
          }).catch((error) => {
            message.error(error);
          });
        }
      },
    }
  }

  const toolbarConfig: Partial<IToolbarConfig> = {
    toolbarKeys: props?.readOnly ? [] : [
      'bold',
      'underline',
      'fontSize',
      'fontFamily',
      {
        key: 'group-justify', // 必填，要以 group 开头
        title: '对齐方式', // 必填
        menuKeys: ['justifyLeft', 'justifyRight', 'justifyCenter'], // 下级菜单 key ，必填
      },
      'divider',
      '|',
      'uploadImage',
      'insertTable',
    ],
  }

  //销毁 editor
  useEffect(() => {
    return () => {
      if (!editor) return
      editor.destroy()
      setEditor(undefined)
    }
  }, [editor])

  useEffect(() => {
    (async () => {
      let result = await parseHtml(imgRoute, props?.value);
      setHtml(result.innerHtml);
      if (result.imgRoute) {
        setImgRoute(result.imgRoute)
      }
    })()
  }, [props?.value])

  const defaultStyle = {height: '120px', overflowY: 'hidden', resize: 'vertical'}

  return (
    <>
      <div
        style={{border: '1px solid #ccc', zIndex: 100000}}
        className={props?.readOnly ? 'wdi-editor-readonly' : ''}
        onClick={props.onClick}
      >
        <Toolbar
          editor={editor ?? null}
          defaultConfig={toolbarConfig}
          mode="default"
          style={props.readOnly ? {} : {borderBottom: '1px solid #ccc'}}
        />
        <Editor
          defaultConfig={editorConfig}
          value={html}
          mode="default"
          style={Object.assign(defaultStyle, (props.style ?? {}))}
        />
      </div>
    </>
  )
});



